import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Calendar as CalendarIcon } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { formatDate, formatTime, formatDay, getRequestOptions } from '@/lib/utils';
import { format } from "date-fns";
import ClassCard from "./ClassCard";
import LoadingSkeleton from "@/components/LoadingSkeleton";
import { useTranslation } from 'react-i18next';

interface UpcomingClassesProps {
  onEdit: (classItem: any) => void;
}

const UpcomingClasses = forwardRef(({ onEdit }: UpcomingClassesProps, ref) => {
  const { t } = useTranslation();
  const [date, setDate] = useState<Date>();
  const [availableClasses, setAvailableClasses] = useState<any[]>([]);
  const [groupedClasses, setGroupedClasses] = useState<GroupedClasses>({});
  const [sortedDates, setSortedDates] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  interface GroupedClasses {
    [key: string]: typeof availableClasses[0][]; // Key is a date string, value is an array of class items
  }

  const fetchClasses = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_BOOKING_CLASSES_ENDPOINT}` + '?type=upcoming', getRequestOptions());

      if (!response.ok) {
        throw new Error('Failed to fetch classes');
      }

      const data = await response.json(); // Parse JSON data

      const filteredClasses = date
        ? data.filter((classItem) => {
          const classDate = new Date(classItem.date);
          return (
            classDate.getMonth() === date.getMonth() &&
            classDate.getFullYear() === date.getFullYear()
          );
        })
        : data;

      // Group classes by date
      const grouped: GroupedClasses = filteredClasses.reduce(
        (groups, classItem) => {
          const dateKey = new Date(classItem.classDateTime).toISOString().split("T")[0]; // Extract YYYY-MM-DD
          if (!groups[dateKey]) {
            groups[dateKey] = [];
          }
          groups[dateKey].push(classItem);
          return groups;
        },
        {} as GroupedClasses
      );

      // Sort dates
      const sorted = Object.keys(grouped).sort(
        (a, b) => new Date(a).getTime() - new Date(b).getTime()
      );
      // Update states
      setGroupedClasses(grouped);
      setSortedDates(sorted);
      setAvailableClasses(filteredClasses); // Store the retrieved classes in state

    } catch (error) {
      console.error('Error fetching classes:', error);
    }
    finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchClasses();
  }, []);

  // Expose the `fetchClasses` function via the ref
  useImperativeHandle(ref, () => ({
    handleRefresh: fetchClasses,
  }));

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">{t('dashboard.upcomingClasses')}</h2>
        {/* <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "justify-start text-left font-normal",
                !date && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {date ? format(date, "MMMM yyyy") : t('common.pickMonth')}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              initialFocus
            />
          </PopoverContent>
        </Popover> */}
      </div>

      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        availableClasses.length === 0 ? (
          <Alert className="max-w-lg mx-auto mb-8">
            <AlertDescription>
              {date ? t('classes.noClassesAvailableClearFilter') : t('classes.noClassesAvailable')}
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-8">
            {sortedDates.map((dateKey) => (
              <div key={dateKey} className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-700 py-2 px-4 bg-gray-50 rounded-lg border-l-4 border-red-500 shadow-md">
                  {formatDay(dateKey) + ' - ' + formatDate(dateKey)}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {groupedClasses[dateKey].map((classItem) => (
                    <ClassCard
                      key={classItem.id}
                      classItem={classItem}
                      onEdit={onEdit}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
});

export default UpcomingClasses;